// /src/data/books/FundametosBlockchain/data/cap12.js
const CAP12 = {
  id: "fundamentos-blockchain-12",
  slug: "propiedades-deseadas-nfts",
  title: "Propiedades Deseadas de los NFTs",
  sections: [
    {
      id: "hero",
      heading: "Propiedades Deseadas de los NFTs",
      text: `Los NFT representan activos digitales únicos dentro de aplicaciones descentralizadas (dApps),
y heredan las propiedades esenciales de los libros mayores públicos sobre los que se ejecutan.
Estas características garantizan transparencia, resiliencia, trazabilidad y confianza en cada interacción.
A continuación se describen, en detalle, las propiedades más relevantes que todo sistema NFT debería cumplir.`
    },

    /* =========================
       1) VERIFICABILIDAD
       ========================= */
    {
      id: "verificabilidad",
      heading: "Verificabilidad",
      text: `La verificabilidad asegura que cualquier persona pueda comprobar de manera independiente la autenticidad,
propiedad y existencia de un NFT sin necesidad de confiar en un intermediario. 
El token y sus metadatos están registrados en la cadena pública, lo que permite validar el hash del contenido, 
confirmar la dirección propietaria actual, y auditar el historial de transferencias. 
Esta transparencia elimina ambigüedades y facilita la detección de falsificaciones o duplicados. 
Los exploradores blockchain y las APIs públicas ofrecen visibilidad inmediata de cada NFT, su tokenID,
su contrato de emisión, y su estado actual, reforzando la confianza del usuario y de los mercados.`
    },

    /* =========================
       2) EJECUCIÓN TRANSPARENTE
       ========================= */
    {
      id: "ejecucion-transparente",
      heading: "Ejecución Transparente",
      text: `Todas las acciones relacionadas con NFTs —acuñación (mint), compra, venta, quema o transferencia— 
se ejecutan mediante contratos inteligentes cuyas funciones y eventos son visibles públicamente en la blockchain.
Cada paso deja trazas verificables: registros de entrada, parámetros, gas utilizado y eventos emitidos. 
Los marketplaces y las plataformas de análisis pueden rastrear de forma exacta la actividad y generar informes públicos. 
La transparencia disuade prácticas fraudulentas y fortalece la confianza del ecosistema, ya que cualquier usuario 
puede auditar en tiempo real las operaciones de un NFT o una colección completa.`
    },

    /* =========================
       3) DISPONIBILIDAD
       ========================= */
    {
      id: "disponibilidad",
      heading: "Disponibilidad y permanencia",
      text: `Una propiedad crítica de los NFT es su disponibilidad continua. 
El sistema blockchain opera de manera descentralizada, eliminando puntos únicos de falla.
Mientras al menos un nodo conserve la cadena, los datos de propiedad y transacciones seguirán accesibles. 
Incluso si un marketplace o frontend desaparece, el NFT puede consultarse directamente en la red o desde otros servicios. 
Además, los contenidos y metadatos almacenados en IPFS o Arweave refuerzan la persistencia a largo plazo.
En esencia, los NFT permanecen disponibles para su visualización, transferencia y verificación, garantizando la longevidad del activo digital.`
    },

    /* =========================
       4) RESISTENCIA A LA MANIPULACIÓN
       ========================= */
    {
      id: "resistencia-manipulacion",
      heading: "Resistencia a la Manipulación",
      text: `Los datos que componen la identidad del NFT —su tokenID, propietario, y metadatos asociados— 
se registran de forma inmutable en la blockchain. 
Una vez confirmada una transacción, su alteración implicaría reescribir la historia de la cadena,
lo cual es computacionalmente inviable en redes seguras como Ethereum o Polygon. 
Esta resistencia protege contra modificaciones maliciosas, falsificación de propiedad o eliminación arbitraria.
Además, el uso de hashes criptográficos asegura la integridad del contenido: si un archivo o metadata fuera alterado, 
el hash ya no coincidiría con el valor registrado, señalando inmediatamente la manipulación.`
    },

    /* =========================
       5) FACILIDAD DE USO
       ========================= */
    {
      id: "facilidad-de-uso",
      heading: "Facilidad de Uso y Accesibilidad",
      text: `El éxito de un NFT depende en gran medida de su usabilidad. 
Cada token debe ofrecer información clara, actualizada y accesible sobre su propietario, 
su procedencia y sus atributos. Las interfaces deben permitir consultas simples y rápidas,
sin requerir conocimientos técnicos avanzados. 
Los estándares ERC-721 y ERC-1155 proveen métodos de lectura estandarizados (como tokenURI y balanceOf),
que simplifican la integración con monederos y marketplaces. 
Además, la compatibilidad con exploradores, APIs y widgets visuales permite una experiencia fluida,
reduciendo fricciones para el usuario final y ampliando la adopción masiva.`
    },

    /* =========================
       6) ATOMICIDAD (ACID)
       ========================= */
    {
      id: "atomicidad",
      heading: "Atomicidad y consistencia transaccional (ACID)",
      text: `Las operaciones sobre NFTs deben ejecutarse bajo principios de atomicidad, consistencia, aislamiento y durabilidad (ACID),
garantizando que los intercambios se completen correctamente o no se apliquen en absoluto.
Una transacción de comercio o transferencia combina acciones interdependientes: validación del propietario,
transferencia de fondos, actualización del registro de propiedad y emisión de eventos. 
Todo el conjunto se procesa como una unidad indivisible; si un paso falla, la transacción completa se revierte,
evitando estados inconsistentes. 
El mismo estado compartido entre los nodos asegura que todos los participantes vean resultados coherentes
y evita desincronización o doble gasto.`
    },

    /* =========================
       7) NEGOCIABILIDAD
       ========================= */
    {
      id: "negociabilidad",
      heading: "Negociabilidad y transferibilidad",
      text: `La negociabilidad convierte a los NFTs en activos dinámicos dentro del ecosistema digital. 
Cualquier NFT puede transferirse, venderse o intercambiarse libremente, 
siempre que se respeten las reglas del contrato y las aprobaciones vigentes. 
Esta flexibilidad permite mercados secundarios activos donde coleccionistas e inversionistas 
pueden intercambiar tokens en tiempo real. 
Los estándares ERC-721 y ERC-1155 garantizan interoperabilidad entre contratos y plataformas,
mientras que extensiones como ERC-2981 (regalías) o ERC-4907 (alquiler) amplían las posibilidades comerciales. 
De este modo, los NFTs no solo representan propiedad, sino también liquidez, utilidad y participación
en ecosistemas económicos descentralizados.`
    },

    /* =========================
       8) CONCLUSIÓN
       ========================= */
    {
      id: "conclusion",
      heading: "Conclusión",
      text: `Las propiedades deseadas de los NFTs combinan transparencia, seguridad, disponibilidad y eficiencia.
Gracias a la verificabilidad, la ejecución pública, la resistencia a la manipulación y la atomicidad,
los NFTs se consolidan como activos confiables y autónomos dentro de la economía digital.
Un diseño que cumpla con estos principios no solo asegura la integridad técnica,
sino que fomenta la confianza de los usuarios y la sostenibilidad a largo plazo del ecosistema.`
    }
  ]
};

export default CAP12;
